import { useCallback } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Button, Card, Header, Icon, Message } from "simplydo/react/ui";

import { useAppSelector } from "store";
import util from "utils/utils";

import { UserChip } from "components/lib/Chips";

import InviteeChip from "./InviteeChip";
import type { InviteCardProps } from "./types";

const ProjectInviteCard = ({
  invitation,
  acceptInvite,
  rejectInvite,
  removeInvite,
  cardView = true,
}: InviteCardProps) => {
  const { inviterUser, inviteeUser, invitee, idea, createdByCurrentUser } =
    invitation;
  const user = useAppSelector((state) => state.user);
  const { t } = useTranslation();

  const accept = useCallback(() => {
    acceptInvite(
      invitation,
      () => {
        toast.success(`Assigned to ${t("generic.idea")}`);
      },
      () => toast.error(`Error while assigning to ${t("generic.idea")}`),
    );
  }, [acceptInvite, invitation, t]);

  const reject = useCallback(() => {
    util
      .confirm(
        `Reject ${t("generic.idea")} assignment`,
        `Are you sure you want to reject the invitation from ${inviterUser?.profile?.fullName} to manage ${idea?.name || `Deleted ${t("generic.idea")}`} on the project board?`,
      )
      .then(() => {
        rejectInvite(
          invitation,
          () => {
            toast("Invitation rejected");
          },
          () => toast.error("Error rejecting invite"),
        );
      })
      .catch(() => {});
  }, [rejectInvite, invitation, inviterUser?.profile?.fullName, idea?.name, t]);

  const remove = useCallback(() => {
    util
      .confirm(
        "Remove assignment",
        `Are you sure you want to remove this invitation to let ${inviteeUser?.profile?.fullName || invitee} project manage the ${t("generic.idea")} "${idea?.name || `Deleted ${t("generic.idea")}`}"?`,
      )
      .then(() => {
        removeInvite(
          invitation,
          () => {
            toast("Invitation removed");
          },
          () => toast.error("Error removing invite"),
        );
      })
      .catch(() => {});
  }, [
    removeInvite,
    invitation,
    idea?.name,
    inviteeUser?.profile?.fullName,
    invitee,
    t,
  ]);

  const canAccept = invitation.canAccept && !!idea;

  return (
    <div
      style={
        cardView
          ? {
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }
          : {}
      }
    >
      <div>
        {cardView ? (
          <Header
            style={{ marginBottom: "0.3em" }}
            as={Link}
            disabled={!idea}
            to={idea ? `/ideas/${idea._id}` : null}
          >
            {idea?.name || `Deleted ${t("generic.idea")}`}
          </Header>
        ) : (
          <Header as="h2" icon textAlign="center">
            <Icon name="user plus" circular />
            <Header.Content>
              Project manage {idea?.name || `Deleted ${t("generic.idea")}`}
            </Header.Content>
          </Header>
        )}
        <Card.Description>
          {createdByCurrentUser ? (
            <InviteeChip
              invitation={invitation}
              preText="You invited"
              postText={`to project manage this ${t("generic.idea")}`}
            />
          ) : (
            <UserChip
              compact="very"
              user={inviterUser}
              postText={`has invited you to project manage this ${t("generic.idea")}`}
            />
          )}
        </Card.Description>
      </div>
      <Card.Content extra>
        <div
          className="ui"
          style={
            cardView
              ? {}
              : { marginTop: 20, display: "flex", justifyContent: "flex-end" }
          }
        >
          {invitation.acceptedAt && idea && (
            <Button
              basic
              color="green"
              as={Link}
              to={`/challenges/${idea?.challenge}/board?idea=${idea?._id}`}
            >
              Go to board
            </Button>
          )}
          {!invitation.acceptedAt && (
            <>
              {!idea && (
                <Message error style={{ width: "100%" }}>
                  This invitation to project manage is no longer valid because
                  the {t("generic.idea")} has been deleted.
                </Message>
              )}
              {user && invitation.canRemove ? (
                <Button
                  basic
                  color={createdByCurrentUser ? undefined : "red"}
                  onClick={createdByCurrentUser ? remove : reject}
                >
                  {createdByCurrentUser ? "Remove" : "Reject"}
                </Button>
              ) : null}
              {user && canAccept ? (
                <Button basic color="green" onClick={accept}>
                  Accept
                </Button>
              ) : null}
            </>
          )}
        </div>
      </Card.Content>
    </div>
  );
};

export default ProjectInviteCard;
